/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Copy,
  Filter,
  Search,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { db } from '../config/firebaseConfig';
import FirestoreTimestampToDate from './utils/FirebaseTimetoDate';

const Transaction = ({ embedded = false }) => {
  const [id, setId] = useState(localStorage.getItem('roomId') || '');
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('hash');

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Failed to copy')
    );
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const transactionsCollection = db
          .collection('rooms')
          .doc(id)
          .collection('transactions');
        const snapshot = await transactionsCollection.get();
        const transactionsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          timestamp: FirestoreTimestampToDate(doc.data().timestamp),
          id: doc.id,
        }));
        setTransactions(transactionsData);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };

    if (id) fetchTransactions();
  }, [id]);

  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true;
    try {
      const term = searchTerm.toLowerCase();
      switch (filter) {
        case 'hash':
          return transaction.id?.toLowerCase().includes(term);
        case 'sender':
          return transaction.sender?.toLowerCase().includes(term);
        case 'receiver':
          return transaction.receiver?.toLowerCase().includes(term);
        case 'status':
          return transaction.status?.toLowerCase().includes(term);
        case 'timestamp':
          return transaction.timestamp?.toLowerCase().includes(term);
        case 'amount':
          return transaction.amount?.toString().includes(term);
        default:
          return true;
      }
    } catch (error) {
      console.error('Filter error:', error);
      return false;
    }
  });

  const renderTrimmedAddress = (address) => (
    <div
      className="flex items-center gap-2 group cursor-pointer"
      onClick={() => copyToClipboard(address)}
    >
      <span className="font-mono text-white/90 group-hover:text-[#6C4FFF] transition-colors">
        {address?.substring(0, 8)}...{address?.substring(address.length - 4)}
      </span>
      <Copy
        size={12}
        className="text-white/20 group-hover:text-white/60 transition-colors opacity-0 group-hover:opacity-100"
      />
    </div>
  );

  return (
    <section
      className={embedded ? 'p-0' : 'min-h-screen pt-8 pb-12 px-6 bg-[#0B0E14]'}
    >
      <div className={embedded ? 'text-white' : 'max-w-7xl mx-auto text-white'}>
        {!embedded && (
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-white mb-2">Transactions</h1>
            <p className="text-white/40">
              Real-time explorer for network activity and blocks.
            </p>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-[#6C4FFF] transition-colors"
              size={18}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by ID, address, or amount..."
              className="w-full bg-[#1E2330] border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-white text-sm outline-none focus:border-[#6C4FFF] focus:ring-1 focus:ring-[#6C4FFF] transition-all placeholder:text-white/30"
            />
          </div>
          <div className="relative min-w-[180px]">
            <Filter
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
              size={16}
            />
            <select
              value={filter}
              onChange={handleFilterChange}
              className="w-full appearance-none bg-[#1E2330] border border-white/10 rounded-xl pl-11 pr-10 py-3.5 text-white text-sm outline-none focus:border-[#6C4FFF] transition-all cursor-pointer hover:border-white/20"
            >
              <option value="hash">Transaction ID</option>
              <option value="sender">Sender</option>
              <option value="receiver">Receiver</option>
              <option value="status">Status</option>
              <option value="timestamp">Timestamp</option>
              <option value="amount">Amount</option>
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none border-l border-white/10 pl-3">
              <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-white/40"></div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-[#1E2330]/50 backdrop-blur rounded-2xl border border-white/5 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5 bg-white/[0.02]">
                  <th className="text-left text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Transaction ID
                  </th>
                  <th className="text-left text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Sender
                  </th>
                  <th className="text-left text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Receiver
                  </th>
                  <th className="text-left text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Status
                  </th>
                  <th className="text-left text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Timestamp
                  </th>
                  <th className="text-right text-white/40 text-xs font-bold uppercase tracking-wider py-5 px-6">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredTransactions && filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction, index) => (
                    <tr
                      key={index}
                      className="hover:bg-white/[0.02] transition-colors group"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-white/5 text-white/60">
                            <ArrowUpRight size={16} />
                          </div>
                          <div className="flex flex-col">
                            <span
                              className="text-white font-medium text-sm font-mono truncate max-w-[120px]"
                              title={transaction.id}
                            >
                              {transaction.id}
                            </span>
                            <span className="text-xs text-white/30 hidden sm:inline-block">
                              Transfer
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(transaction.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all"
                          >
                            <Copy size={12} className="text-white/40" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {renderTrimmedAddress(transaction.sender)}
                      </td>
                      <td className="py-4 px-6">
                        {renderTrimmedAddress(transaction.receiver)}
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                            transaction.status === 'Verified'
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                          }`}
                        >
                          {transaction.status === 'Verified' ? (
                            <CheckCircle2 size={12} />
                          ) : (
                            <Clock size={12} />
                          )}
                          {transaction.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-white/60 text-sm">
                        {transaction.timestamp}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <span className="text-white font-bold text-sm">
                          {transaction.amount}
                        </span>
                        <span className="text-white/40 text-xs ml-1">AST</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-16 text-center">
                      <div className="flex flex-col items-center justify-center text-white/30">
                        <Search size={48} className="mb-4 opacity-50" />
                        <p className="text-lg font-medium">
                          No Transactions Found
                        </p>
                        <p className="text-sm mt-1">
                          Try adjusting your search or filters
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Transaction;
